import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { DollarSign, Calendar, Percent, ShoppingCart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Commissions = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      const response = await fetch(`${API}/commissions/summary`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Rate tiers from the user's agency settings (fallback to EduPlan defaults)
  const acRates = summary?.ac_rates || { seuil_1: 22, seuil_2: 25, seuil_3: 27 };
  const acThresholds = summary?.ac_thresholds || { tier_1_max: 3500, tier_2_max: 6500 };

  const getRateTierInfo = (ca) => {
    if (ca >= acThresholds.tier_2_max) {
      return { rate: acRates.seuil_3, next: null, remaining: 0, nextThreshold: null };
    }
    if (ca >= acThresholds.tier_1_max) {
      return { rate: acRates.seuil_2, next: acRates.seuil_3, remaining: acThresholds.tier_2_max - ca, nextThreshold: acThresholds.tier_2_max };
    }
    return { rate: acRates.seuil_1, next: acRates.seuil_2, remaining: acThresholds.tier_1_max - ca, nextThreshold: acThresholds.tier_1_max };
  };

  const tierInfo = getRateTierInfo(summary?.monthly_ca || 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="commissions-title">
          Mes commissions
        </h1>
        <p className="text-slate-500 mt-1">Suivi de vos commissions et paliers</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm" data-testid="monthly-ca-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <ShoppingCart className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">CA du mois</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {summary?.monthly_ca?.toLocaleString('fr-FR') || 0} EUR
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm" data-testid="rate-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-100">
              <Percent className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Taux actuel</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {summary?.rate || tierInfo.rate}%
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm" data-testid="commission-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Commission totale</p>
              <p className="text-xl font-bold text-emerald-600">
                {summary?.total?.toLocaleString('fr-FR', {minimumFractionDigits: 2}) || '0.00'} EUR
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm" data-testid="pending-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">A percevoir</p>
              <p className="text-xl font-bold text-amber-600">
                {summary?.pending?.toLocaleString('fr-FR', {minimumFractionDigits: 2}) || '0.00'} EUR
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm" data-testid="paid-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-100">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Deja paye</p>
              <p className="text-xl font-bold text-green-600">
                {summary?.paid?.toLocaleString('fr-FR', {minimumFractionDigits: 2}) || '0.00'} EUR
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rate tiers */}
      <Card className="border-0 shadow-sm" data-testid="rate-tiers-card">
        <CardHeader>
          <CardTitle className="text-lg font-[Manrope]">Bareme des commissions</CardTitle>
          <CardDescription>Votre taux augmente avec votre CA mensuel</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Current tier progress */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium">Progression vers le palier suivant</span>
                {tierInfo.next ? (
                  <Badge className="bg-indigo-100 text-indigo-700">
                    {tierInfo.remaining.toLocaleString('fr-FR')} EUR restants pour {tierInfo.next}%
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    Palier maximum atteint !
                  </Badge>
                )}
              </div>
              <Progress 
                value={tierInfo.nextThreshold ? Math.min(100, ((summary?.monthly_ca || 0) / tierInfo.nextThreshold) * 100) : 100} 
                className="h-3"
              />
            </div>
            
            {/* Tier table */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className={`p-4 rounded-xl border-2 ${tierInfo.rate === acRates.seuil_1 ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                <div className="text-center">
                  <p className="text-3xl font-bold text-indigo-600">{acRates.seuil_1}%</p>
                  <p className="text-sm text-slate-500 mt-1">CA 0 - {acThresholds.tier_1_max.toLocaleString('fr-FR')} EUR</p>
                  {tierInfo.rate === acRates.seuil_1 && <Badge className="mt-2 bg-indigo-600">Actuel</Badge>}
                </div>
              </div>
              <div className={`p-4 rounded-xl border-2 ${tierInfo.rate === acRates.seuil_2 ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                <div className="text-center">
                  <p className="text-3xl font-bold text-purple-600">{acRates.seuil_2}%</p>
                  <p className="text-sm text-slate-500 mt-1">CA {acThresholds.tier_1_max.toLocaleString('fr-FR')} - {acThresholds.tier_2_max.toLocaleString('fr-FR')} EUR</p>
                  {tierInfo.rate === acRates.seuil_2 && <Badge className="mt-2 bg-purple-600">Actuel</Badge>}
                </div>
              </div>
              <div className={`p-4 rounded-xl border-2 ${tierInfo.rate === acRates.seuil_3 ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}>
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">{acRates.seuil_3}%</p>
                  <p className="text-sm text-slate-500 mt-1">CA {acThresholds.tier_2_max.toLocaleString('fr-FR')}+ EUR</p>
                  {tierInfo.rate === acRates.seuil_3 && <Badge className="mt-2 bg-emerald-600">Actuel</Badge>}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment info */}
      <Card className="border-0 shadow-sm bg-slate-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Calendar className="w-10 h-10 text-indigo-600" />
            <div>
              <h3 className="font-semibold text-slate-900">Prochain versement</h3>
              <p className="text-slate-500">
                Les commissions sont versees le 05 de chaque mois
              </p>
              {summary?.next_payment_date && (
                <p className="text-sm text-indigo-600 mt-1">
                  Prochaine date: {new Date(summary.next_payment_date).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
